const normalizeText = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim().toLowerCase();
  return trimmed || '';
};

const parseMessagePayload = (message) => {
  if (!message || typeof message !== 'object') return null;
  const raw = message.message_payload ?? message.messagePayload ?? null;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const coerceString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const pickPayloadString = (payload, keys) => {
  if (!payload || typeof payload !== 'object') return '';
  for (const key of keys) {
    const value = coerceString(payload[key]);
    if (value) return value;
  }
  return '';
};

const getTimelineEventType = (message, payload) => {
  const direct =
    message?.timeline_event_type ??
    message?.timelineEventType ??
    message?.lifecycle_event_type ??
    message?.lifecycleEventType ??
    message?.event_type ??
    message?.eventType ??
    payload?.timeline_event_type ??
    payload?.timelineEventType ??
    payload?.event_type ??
    payload?.eventType ??
    null;
  const normalizedDirect = normalizeText(direct);
  if (normalizedDirect) return normalizedDirect;
  return normalizeText(payload?.action);
};

const VISIBLE_TIMELINE_EVENTS = new Set([
  'conversation_started_by_attendant',
  'attendance_assumed',
  'attendance_started',
  'attendant_assignment',
  'attendant_transferred',
  'team_assigned',
  'team_transferred',
  'assigned_to_team',
  'released_to_team',
  'transferred_to_team_by_workflow_timeout',
  'attendance_closed',
  'attendance_closed_inactivity',
  'conversation_closed',
  'conversation_closed_inactivity',
  'closed',
  'closed_by_bot',
  'closed_by_inactivity',
  'closed_by_workflow_inactivity_trigger',
  'closed_by_workflow_timeout_rule',
  'attendance_canceled',
  'conversation_canceled',
  'canceled',
]);

const HIDDEN_TIMELINE_EVENTS = new Set([
  'queued_for_human',
  'first_human_response',
  'queue_entered',
  'queue_left',
  'transfer_requested',
  'transfer_accepted',
  'transfer_rejected',
  'handling_status_changed',
  'workflow_waiting_attendant_queue',
  'workflow_completed_waiting_attendant',
]);

const isTeamTimelineStatus = (message) => normalizeText(message?.status) === 'team_transfer';

const isLegacyVisibleSystemTimelineText = (message) => {
  const text = normalizeText(message?.message ?? message?.text ?? message?.body);
  if (!text) return false;
  return (
    text.includes('iniciou o atendimento') ||
    text.includes('assumiu o atendimento') ||
    text.includes('transferid') ||
    text.includes('direcionou a conversa para o time') ||
    text.includes('finalizou o atendimento') ||
    text.includes('encerrado por inatividade') ||
    text.includes('finalizado automaticamente') ||
    text.includes('cancelou o atendimento') ||
    text.includes('atendimento cancelado')
  );
};

export const isVisibleChatTimelineEvent = (message) => {
  if (!message || typeof message !== 'object') return false;
  if (!isSystemMessage(message)) return true;

  const payload = parseMessagePayload(message);
  const eventType = getTimelineEventType(message, payload);
  if (HIDDEN_TIMELINE_EVENTS.has(eventType)) return false;
  if (VISIBLE_TIMELINE_EVENTS.has(eventType)) return true;
  if (isTeamTimelineStatus(message)) return true;

  return isLegacyVisibleSystemTimelineText(message);
};

const resolveAttendantTimelineLabel = (payload) => {
  const toName = pickPayloadString(payload, [
    'to_attendant_name',
    'toAttendantName',
    'attendant_name',
    'attendantName',
    'accepted_by_attendant_name',
    'acceptedByAttendantName',
    'fallback_attendant_name',
    'fallbackAttendantName',
  ]);
  const fromName = pickPayloadString(payload, [
    'from_attendant_name',
    'fromAttendantName',
  ]);
  if (fromName && toName && fromName.toLowerCase() !== toName.toLowerCase()) {
    return `Atendimento transferido para ${toName}.`;
  }
  if (toName) return `${toName} assumiu o atendimento.`;
  return 'Atendimento humano iniciado.';
};

const resolveConversationStartedByAttendantLabel = (payload) => {
  const actorName = pickPayloadString(payload, [
    'attendant_name',
    'attendantName',
    'performed_by_attendant_name',
    'performedByAttendantName',
    'actor_name',
    'actorName',
  ]);
  if (actorName) return `${actorName} iniciou o atendimento.`;
  return 'Atendimento iniciado pelo atendente.';
};

const resolveTeamTimelineLabel = (payload) => {
  const toName = pickPayloadString(payload, ['team_name', 'teamName', 'to_team_name', 'toTeamName']);
  const fromName = pickPayloadString(payload, ['from_team_name', 'fromTeamName']);
  if (fromName && toName && fromName.toLowerCase() !== toName.toLowerCase()) {
    return `Atendimento transferido de ${fromName} para ${toName}.`;
  }
  if (toName) return `Atendimento transferido para ${toName}.`;
  return 'Atendimento transferido para outra fila.';
};

const resolveClosedTimelineLabel = (payload, eventType) => {
  const source = normalizeText(payload?.source);
  const reason = normalizeText(payload?.reason);
  if (
    eventType === 'attendance_closed_inactivity' ||
    eventType === 'conversation_closed_inactivity' ||
    eventType === 'closed_by_inactivity' ||
    eventType === 'closed_by_workflow_inactivity_trigger' ||
    source.includes('inactivity') ||
    reason.includes('inactivity') ||
    reason.includes('inatividade')
  ) {
    return 'Atendimento encerrado por inatividade.';
  }
  if (eventType === 'closed_by_workflow_timeout_rule' || source.includes('workflow_timeout')) {
    return 'Atendimento encerrado por timeout do workflow.';
  }
  const actorName = pickPayloadString(payload, [
    'performed_by_attendant_name',
    'performedByAttendantName',
    'attendant_name',
    'attendantName',
    'actor_name',
    'actorName',
  ]);
  if (actorName) return `${actorName} finalizou o atendimento.`;
  return 'Atendimento encerrado.';
};

const resolveCanceledTimelineLabel = (payload) => {
  const actorName = pickPayloadString(payload, [
    'performed_by_attendant_name',
    'performedByAttendantName',
    'attendant_name',
    'attendantName',
    'actor_name',
    'actorName',
  ]);
  if (actorName) return `${actorName} cancelou o atendimento.`;
  return 'Atendimento cancelado.';
};

const normalizeLegacyTimelineLabel = (fallbackLabel) => {
  const label = coerceString(fallbackLabel);
  if (!label) return '';
  const normalized = normalizeText(label);
  if (normalized.includes('inatividade')) {
    return 'Atendimento encerrado por inatividade.';
  }
  if (
    normalized.includes('regra de tempo do workflow') ||
    normalized.includes('timeout do workflow')
  ) {
    return 'Atendimento encerrado por timeout do workflow.';
  }
  const assumedMatch = label.match(/^(.+?)\s+assumiu o atendimento(?:\.|$)/i);
  if (assumedMatch?.[1]) {
    return `${assumedMatch[1].trim()} assumiu o atendimento.`;
  }
  if (normalized.includes('transferida para outro time')) {
    return 'Atendimento transferido para outra fila.';
  }
  return label;
};

export const resolveChatTimelineEventLabel = (message, fallbackLabel = '') => {
  if (!message || typeof message !== 'object' || !isSystemMessage(message)) {
    return fallbackLabel;
  }

  const payload = parseMessagePayload(message) || {};
  const eventType = getTimelineEventType(message, payload);
  const status = normalizeText(message?.status);
  if (eventType === 'conversation_started_by_attendant') {
    return resolveConversationStartedByAttendantLabel(payload);
  }
  if (
    eventType === 'attendance_assumed' ||
    eventType === 'attendance_started' ||
    eventType === 'attendant_assignment' ||
    eventType === 'attendant_transferred'
  ) {
    return resolveAttendantTimelineLabel(payload);
  }
  if (
    eventType === 'team_assigned' ||
    eventType === 'team_transferred' ||
    eventType === 'assigned_to_team' ||
    eventType === 'released_to_team' ||
    eventType === 'transferred_to_team_by_workflow_timeout' ||
    status === 'team_transfer'
  ) {
    return resolveTeamTimelineLabel(payload);
  }
  if (
    eventType === 'attendance_canceled' ||
    eventType === 'conversation_canceled' ||
    eventType === 'canceled'
  ) {
    return resolveCanceledTimelineLabel(payload);
  }
  if (
    eventType === 'attendance_closed' ||
    eventType === 'attendance_closed_inactivity' ||
    eventType === 'conversation_closed' ||
    eventType === 'conversation_closed_inactivity' ||
    eventType === 'closed' ||
    eventType === 'closed_by_bot' ||
    eventType === 'closed_by_inactivity' ||
    eventType === 'closed_by_workflow_inactivity_trigger' ||
    eventType === 'closed_by_workflow_timeout_rule'
  ) {
    return resolveClosedTimelineLabel(payload, eventType);
  }

  return normalizeLegacyTimelineLabel(fallbackLabel);
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const coerceOptionalBoolean = (value) => {
  if (value === true || value === false) return value;
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
    return null;
  }
  if (typeof value !== 'string') return null;
  const normalized = normalizeText(value);
  if (!normalized) return null;
  if (['1', 'true', 'yes', 'sim'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'nao'].includes(normalized)) return false;
  return null;
};

const resolveFromMeFlag = (message) => {
  if (!message || typeof message !== 'object') return null;
  const candidates = [
    message.message_key_from_me,
    message.messageKeyFromMe,
    message.is_from_me,
    message.isFromMe,
    message.from_me,
    message.fromMe,
  ];
  for (const candidate of candidates) {
    const parsed = coerceOptionalBoolean(candidate);
    if (parsed !== null) return parsed;
  }
  return null;
};

export const normalizeAuthorType = (message) => {
  if (!message || typeof message !== 'object') return '';
  return normalizeText(message.author_type ?? message.authorType);
};

export const getSenderLower = (message) => {
  if (!message || typeof message !== 'object') return '';
  return normalizeText(message.sender);
};

export const isSystemMessage = (message) => {
  if (!message || typeof message !== 'object') return false;
  if (normalizeText(message.status) === 'system_log') return true;
  const authorType = normalizeAuthorType(message);
  return authorType === 'system';
};

export const isBotMessage = (message) => {
  if (!message || typeof message !== 'object') return false;
  const authorType = normalizeAuthorType(message);
  return authorType === 'bot';
};

export const isAttendantMessage = (message) => {
  if (!message || typeof message !== 'object') return false;
  const authorType = normalizeAuthorType(message);
  if (authorType === 'attendant') return true;
  if (authorType === 'client' || authorType === 'system' || authorType === 'bot') {
    return false;
  }
  const fromMe = resolveFromMeFlag(message);
  if (fromMe === true) return true;
  const attendantId = message.attendant_id ?? message.attendantId;
  if (attendantId === null || attendantId === undefined) return false;
  const normalizedAttendantId = String(attendantId).trim();
  if (!normalizedAttendantId) return false;
  const numericAttendantId = Number(normalizedAttendantId);
  return Number.isFinite(numericAttendantId) && numericAttendantId > 0;
};

export const isClientMessage = (message) => {
  if (!message || typeof message !== 'object') return false;
  const authorType = normalizeAuthorType(message);
  if (authorType) return authorType === 'client';
  if (isSystemMessage(message) || isBotMessage(message) || isAttendantMessage(message)) {
    return false;
  }
  const fromMe = resolveFromMeFlag(message);
  if (fromMe === true) return false;
  return true;
};

export const resolveVirtualAgentLabel = (companyName) => {
  const name = typeof companyName === 'string' ? companyName.trim() : '';
  if (name) return `${name} (Agente Virtual)`;
  return 'Agente Virtual';
};

export const resolveBotDisplayName = (message, fallbackLabel) => {
  const sender =
    message && typeof message === 'object' && typeof message.sender === 'string'
      ? message.sender.trim()
      : '';
  if (sender && sender.toLowerCase() !== 'bot') return sender;
  const fallback = typeof fallbackLabel === 'string' ? fallbackLabel.trim() : '';
  if (fallback) return fallback;
  return resolveVirtualAgentLabel();
};

export const stripAttendantNamePrefix = (text, senderLabel) => {
  if (typeof text !== 'string') return text;
  if (!text.trim()) return text;
  const label = typeof senderLabel === 'string' ? senderLabel.trim() : '';
  if (!label) return text;

  const attendantPrefix = /^(?:attendant|atendente)\s*[:-]?\s*/i;
  let next = text.trimStart();
  let stripped = false;

  if (attendantPrefix.test(next)) {
    next = next.replace(attendantPrefix, '');
    stripped = true;
  }

  const labelPattern = new RegExp(
    `^\\s*\\*?\\s*${escapeRegExp(label)}\\s*\\*?\\s*[:\\-]?\\s*`,
    'i'
  );
  if (labelPattern.test(next)) {
    next = next.replace(labelPattern, '');
    stripped = true;
  }

  return stripped ? next : text;
};
